import { type CSSProperties, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { NotificationApi } from '@/api/v2Client';
import { SubmissionFollowButton } from './SubmissionFollowButton';
import { NotificationItem } from './NotificationItem';

// Phase 5.X-notifications-system — bell dropdown.
//
// Pops out from under the bell icon. Shows the 5 most recent unread
// notifications + a "View all" link to /notifications. The dropdown
// closes on outside-click (handled by the parent BellIcon container).

const DROPDOWN_STYLE: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  left: 0,
  width: 'min(360px, calc(100vw - 80px))',
  maxHeight: 'min(480px, calc(100vh - 80px))',
  overflowY: 'auto',
  background: 'var(--surface, #ffffff)',
  color: 'var(--text, #111827)',
  border: '1px solid var(--border, #e5e7eb)',
  borderRadius: 8,
  boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
  zIndex: 1000,
};

const HEADER_STYLE: CSSProperties = {
  padding: '10px 14px',
  borderBottom: '1px solid var(--border, #e5e7eb)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const EMPTY_STYLE: CSSProperties = {
  padding: 24,
  textAlign: 'center',
  color: 'var(--text-muted, #6b7280)',
  fontSize: 13,
};

const FOOTER_STYLE: CSSProperties = {
  padding: '10px 14px',
  borderTop: '1px solid var(--border, #e5e7eb)',
  textAlign: 'center',
};

const LINK_STYLE: CSSProperties = {
  fontSize: 12,
  color: 'var(--accent, #2563eb)',
  textDecoration: 'none',
  fontWeight: 600,
};

export interface NotificationsDropdownProps {
  readonly currentSubmissionId?: string;
  /** Unread + a few read, newest-first. The dropdown slices to the first 5. */
  readonly notifications: ReadonlyArray<NotificationApi>;
  /** Total unread count (for the header "X unread" hint). */
  readonly unreadCount: number;
  /** Called when an item is clicked. Implementation marks read + navigates if link. */
  readonly onItemClick: (n: NotificationApi) => void;
  /** Called when the "Mark all read" button is clicked. */
  readonly onMarkAllRead: () => void;
  /** Called when the dropdown should close (used by parent on outside-click). */
  readonly onClose: () => void;
}

const DROPDOWN_LIMIT = 5;

export function NotificationsDropdown({
  notifications,
  currentSubmissionId,
  unreadCount,
  onItemClick,
  onMarkAllRead,
  onClose,
}: NotificationsDropdownProps): ReactElement {
  const navigate = useNavigate();
  const slice = notifications.slice(0, DROPDOWN_LIMIT);
  return (
    <div
      role="dialog"
      aria-label="Notifications"
      data-testid="notifications-dropdown"
      style={DROPDOWN_STYLE}
    >
      <div style={HEADER_STYLE}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>
          Notifications
          {unreadCount > 0 && (
            <span style={{ marginLeft: 6, color: 'var(--text-muted, #6b7280)', fontWeight: 400 }}>
              ({unreadCount} unread)
            </span>
          )}
        </span>
        {unreadCount > 0 && (
          <button
            type="button"
            data-testid="notifications-mark-all-read"
            onClick={onMarkAllRead}
            style={{
              fontSize: 11,
              background: 'transparent',
              color: 'var(--accent, #2563eb)',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Mark all read
          </button>
        )}
      </div>
      <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
        {currentSubmissionId ? <>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>Updates for this submission</div>
          <SubmissionFollowButton key={currentSubmissionId} submissionId={currentSubmissionId} />
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '8px 0 0' }}>Classification and compilation summaries, including failures that need attention.</p>
        </> : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Open a submission to turn on its updates.</span>}
      </div>
      {slice.length === 0 ? (
        <div data-testid="notifications-empty" style={EMPTY_STYLE}>
          No notifications yet.
        </div>
      ) : (
        <div>
          {slice.map((n, index) => (
            <NotificationItem
              key={n.id}
              notification={n}
              alternate={index % 2 === 1}
              compact
              onClick={() => {
                onItemClick(n);
                if (n.link) {
                  navigate(n.link);
                  onClose();
                }
              }}
            />
          ))}
        </div>
      )}
      <div style={FOOTER_STYLE}>
        <Link
          to="/notifications"
          data-testid="notifications-view-all"
          onClick={onClose}
          style={LINK_STYLE}
        >
          View all notifications
        </Link>
      </div>
    </div>
  );
}
